import { memo, ReactNode } from "react";

import {
  Check,
  ChevronDown,
} from "@hl/base-components/lib/assets/icons.generated";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  Box,
  createStyles,
  Flex,
  Group,
  Menu,
  Stack,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { NavLink, useLocation } from "react-router-dom";
import useMantineMediaQueries from "~/../../base-components/lib/hooks/useMantineMediaQueries";

import { TOOLS_URL } from "config";

const useStyles = createStyles((theme, _params, getRef) => ({
  menuTarget: {
    ["&:hover"]: {
      [`& .${getRef("child")}`]: {
        paddingTop: 6,
      },
    },
  },
  child: {
    ref: getRef("child"),
    transition: "0.1s ease-in-out",
    "& path": {
      filter: `drop-shadow(0px 2px 2px ${
        theme.colorScheme === "dark"
          ? theme.colors.white[6]
          : theme.colors.black[3]
      })`,
    },
  },
}));

const DashboardContent = ({
  header,
  children,
}: {
  header?: ReactNode;
  children: ReactNode;
}) => {
  const { isMedium } = useMantineMediaQueries();
  const { pathname } = useLocation();
  const { classes } = useStyles();

  return (
    <Stack spacing={32} h="100%">
      <Group position="apart">
        <Menu shadow="md" width={360} offset={8} position="bottom-start">
          <Menu.Target>
            <UnstyledButton className={classes.menuTarget}>
              <Group align="center" spacing={6}>
                <Title order={3} fw={WEIGHT_BOLD}>
                  {headerTitle(pathname)}
                </Title>
                <Flex mt={4} className={classes.child}>
                  <ChevronDown width={32} height={32} />
                </Flex>
              </Group>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            <MenuLink to={TOOLS_URL.home}>Collections</MenuLink>
            {isMedium && (
              <MenuLink to={TOOLS_URL.contract.list}>Contracts</MenuLink>
            )}
            <MenuLink to={TOOLS_URL.gates.list}>Gates</MenuLink>
            {isMedium && <MenuLink to={TOOLS_URL.pages.list}>Pages</MenuLink>}
          </Menu.Dropdown>
        </Menu>
        {header}
      </Group>
      <Box h="100%">{children}</Box>
    </Stack>
  );
};

const MenuLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Menu.Item component="div" p={0}>
    <NavLink to={to} style={{ textDecoration: "none" }}>
      {({ isActive }) => (
        <Title
          order={6}
          fw={WEIGHT_BOLD}
          sx={(theme) => ({
            padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
          })}
        >
          <Group position="apart">
            {children}
            {isActive && <Check width={24} height={24} />}
          </Group>
        </Title>
      )}
    </NavLink>
  </Menu.Item>
);

const headerTitle = (pathname: string) => {
  switch (pathname) {
    case TOOLS_URL.contract.list:
      return "Contracts";
    case TOOLS_URL.gates.list:
      return "Gates";
    case TOOLS_URL.pages.list:
      return "Pages";
    default:
      return "Collections";
  }
};

export default memo(DashboardContent);
